/**
 * Корневой компонент BeepApp Mini App.
 * Управляет аутентификацией и роутингом.
 */
import React, { useEffect, useState } from "react"
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom"
import { useAuthStore } from "./store/authStore"
import { resolveTelegramColorScheme, useTelegram } from "./hooks/useTelegram"
import { garageApi } from "./api/endpoints"
import { t } from "./i18n"
import { SearchPage } from "./pages/SearchPage"
import { GaragePage } from "./pages/GaragePage"
import { AdminPage } from "./pages/AdminPage"
import { AutoclanPage } from "./pages/AutoclanPage"
import { PartnerPage } from "./pages/PartnerPage"
import { GarageEntryPage } from "./pages/GarageEntryPage"
import { CommercialPage } from "./pages/CommercialPage"

function createLaunchId(): string {
  const generated = globalThis.crypto?.randomUUID?.()
  if (generated) return generated
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

function mixTelegramHex(
  base: string | undefined,
  target: string | undefined,
  targetWeight: number,
): string | undefined {
  const parse = (value: string | undefined): [number, number, number] | null => {
    const match = value?.trim().match(/^#([0-9a-f]{6})$/i)
    if (!match) return null
    const hex = match[1]
    return [
      Number.parseInt(hex.slice(0, 2), 16),
      Number.parseInt(hex.slice(2, 4), 16),
      Number.parseInt(hex.slice(4, 6), 16),
    ]
  }

  const from = parse(base)
  const to = parse(target)
  if (!from || !to) return undefined

  const weight = Math.min(1, Math.max(0, targetWeight))
  const channel = (index: number) =>
    Math.round(from[index] * (1 - weight) + to[index] * weight)
      .toString(16)
      .padStart(2, "0")

  return `#${channel(0)}${channel(1)}${channel(2)}`
}

function LoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="text-5xl mb-4">🚗</div>
      <div
        className="inline-block w-8 h-8 border-2 rounded-full animate-spin"
        style={{ borderColor: "var(--bb-figma-action-primary)", borderTopColor: "transparent" }}
      />
      <p className="mt-4 text-sm" style={{ color: "var(--tg-theme-hint-color)" }}>
        {t("common.loading")}
      </p>
    </div>
  )
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4">
      <div className="text-5xl mb-4">⚠️</div>
      <h2 className="text-lg font-bold mb-2">{t("auth.loginErrorTitle")}</h2>
      <p className="text-sm text-center" style={{ color: "var(--tg-theme-hint-color)" }}>
        {message}
      </p>
      <p className="text-xs mt-4 text-center" style={{ color: "var(--tg-theme-hint-color)" }}>
        {t("auth.openViaTelegram")}
      </p>
    </div>
  )
}

function PortraitOnlyScreen() {
  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen px-6 text-center"
      style={{ background: "var(--bb-figma-background-main)", color: "var(--bb-figma-text-primary)" }}
    >
      <p className="text-base font-semibold m-0">{t("device.rotatePortrait")}</p>
    </div>
  )
}

function OwnGarageRoute({
  children,
  requireActive = false,
  matchVehicleParam = false,
}: {
  children: React.ReactNode
  requireActive?: boolean
  matchVehicleParam?: boolean
}) {
  const navigate = useNavigate()
  const { vehicleId } = useParams<{ vehicleId: string }>()
  const [state, setState] = useState<"checking" | "allowed" | "blocked">("checking")

  useEffect(() => {
    let cancelled = false

    const checkAccess = async () => {
      try {
        const garage = await garageApi.getMine()
        const ownsGarage = Boolean(garage.is_owner)
        const activeAllowed = !requireActive || garage.is_active
        const vehicleAllowed = !matchVehicleParam || (
          garage.vehicle_id != null && Number(vehicleId) === garage.vehicle_id
        )

        if (cancelled) return

        if (ownsGarage && activeAllowed && vehicleAllowed) {
          setState("allowed")
          return
        }

        setState("blocked")
        navigate(matchVehicleParam ? "/" : "/garage-entry", { replace: true })
      } catch {
        if (cancelled) return
        setState("blocked")
        navigate("/garage-entry", { replace: true })
      }
    }

    void checkAccess()

    return () => {
      cancelled = true
    }
  }, [navigate, requireActive, matchVehicleParam, vehicleId])

  if (state === "checking") {
    return <LoadingScreen />
  }

  if (state === "blocked") {
    return null
  }

  return <>{children}</>
}

function AppContent() {
  const { login, heartbeat, isAuthenticated, isLoading } = useAuthStore()
  const { initData, tg } = useTelegram()
  const [authError, setAuthError] = useState("")
  const [orientationBlocked, setOrientationBlocked] = useState(false)

  useEffect(() => {
    const mobileTelegram = Boolean(
      tg?.initData && ["android", "ios"].includes((tg.platform || "").toLowerCase()),
    )

    if (!mobileTelegram || !tg) {
      setOrientationBlocked(false)
      return
    }

    // Prevent the Telegram pull-down gesture from minimizing/closing BeepApp.
    // The native close button remains available to the user.
    try { tg.disableVerticalSwipes?.() } catch { /* Best-effort Telegram API call. */ }

    const syncPortraitLock = () => {
      const orientationType = window.screen.orientation?.type || ""
      const portrait = orientationType
        ? orientationType.startsWith("portrait")
        : window.matchMedia
          ? window.matchMedia("(orientation: portrait)").matches
          : window.innerHeight >= window.innerWidth

      setOrientationBlocked(!portrait)

      if (portrait) {
        try { tg.lockOrientation?.() } catch { /* Best-effort Telegram API call. */ }
      } else if (tg.isOrientationLocked) {
        // Telegram locks the *current* orientation. If the app happened to open
        // in landscape, unlock first so the user can rotate to portrait, then lock.
        try { tg.unlockOrientation?.() } catch { /* Best-effort Telegram API call. */ }
      }
    }

    syncPortraitLock()

    const screenOrientation = window.screen.orientation
    window.addEventListener("orientationchange", syncPortraitLock)
    screenOrientation?.addEventListener?.("change", syncPortraitLock)

    return () => {
      window.removeEventListener("orientationchange", syncPortraitLock)
      screenOrientation?.removeEventListener?.("change", syncPortraitLock)
    }
  }, [tg, initData])

  useEffect(() => {
    const root = document.documentElement
    const isTelegramRuntime = Boolean(tg?.initData)

    if (!isTelegramRuntime || !tg) {
      delete root.dataset.bbTelegramWebview
      return
    }

    root.dataset.bbTelegramWebview = "true"

    const setPxVar = (name: string, value: number | undefined) => {
      const safeValue = Number.isFinite(value) ? Math.max(0, Number(value)) : 0
      root.style.setProperty(name, `${safeValue}px`)
    }

    const mobileTelegram = ["android", "ios"].includes((tg.platform || "").toLowerCase())
    let layoutHeight = Math.max(
      0,
      Number(tg.viewportStableHeight || tg.viewportHeight || window.innerHeight) || 0,
    )

    const applyTelegramViewport = () => {
      const reportedHeight = Math.max(
        0,
        Number(tg.viewportStableHeight || tg.viewportHeight || window.innerHeight) || 0,
      )

      // On mobile, Telegram may temporarily report a shorter WebView while the
      // software keyboard animates. The BeepApp layout must not participate in
      // that shrink. We only accept larger heights (for initial tg.expand()).
      if (mobileTelegram) {
        layoutHeight = Math.max(layoutHeight, reportedHeight)
      } else {
        layoutHeight = reportedHeight
      }

      const safe = tg.safeAreaInset
      const contentSafe = tg.contentSafeAreaInset

      setPxVar("--bb-tg-viewport-width", window.innerWidth)
      setPxVar("--bb-tg-viewport-height", layoutHeight)
      setPxVar("--bb-tg-viewport-stable-height", layoutHeight)

      setPxVar("--bb-tg-safe-area-top", safe?.top)
      setPxVar("--bb-tg-safe-area-right", safe?.right)
      setPxVar("--bb-tg-safe-area-bottom", safe?.bottom)
      setPxVar("--bb-tg-safe-area-left", safe?.left)
      setPxVar("--bb-tg-content-safe-area-top", contentSafe?.top)
      setPxVar("--bb-tg-content-safe-area-right", contentSafe?.right)
      setPxVar("--bb-tg-content-safe-area-bottom", contentSafe?.bottom)
      setPxVar("--bb-tg-content-safe-area-left", contentSafe?.left)

      // Telegram viewport and safe-area values are recorded for host/runtime use only.
      // Approved Figma coordinates already include the product safe zones and must not
      // be shifted or trimmed inside Telegram.
    }

    applyTelegramViewport()
    tg.onEvent("viewportChanged", applyTelegramViewport)
    tg.onEvent("safeAreaChanged", applyTelegramViewport)
    tg.onEvent("contentSafeAreaChanged", applyTelegramViewport)
    tg.onEvent("fullscreenChanged", applyTelegramViewport)
    if (!mobileTelegram) window.addEventListener("resize", applyTelegramViewport)

    return () => {
      tg.offEvent("viewportChanged", applyTelegramViewport)
      tg.offEvent("safeAreaChanged", applyTelegramViewport)
      tg.offEvent("contentSafeAreaChanged", applyTelegramViewport)
      tg.offEvent("fullscreenChanged", applyTelegramViewport)
      if (!mobileTelegram) window.removeEventListener("resize", applyTelegramViewport)
      delete root.dataset.bbTelegramWebview
      for (const name of [
        "--bb-tg-viewport-width",
        "--bb-tg-viewport-height",
        "--bb-tg-viewport-stable-height",
        "--bb-tg-safe-area-top",
        "--bb-tg-safe-area-right",
        "--bb-tg-safe-area-bottom",
        "--bb-tg-safe-area-left",
        "--bb-tg-content-safe-area-top",
        "--bb-tg-content-safe-area-right",
        "--bb-tg-content-safe-area-bottom",
        "--bb-tg-content-safe-area-left",
      ]) root.style.removeProperty(name)
    }
  }, [tg, initData])

  useEffect(() => {
    const requestedDevTheme = import.meta.env.DEV
      ? new URLSearchParams(window.location.search).get("theme")?.toLowerCase()
      : null
    const devTheme = requestedDevTheme === "light" || requestedDevTheme === "dark"
      ? requestedDevTheme
      : null

    const isTelegramRuntime = Boolean(tg?.initData)
    if (!isTelegramRuntime && !devTheme) return

    const applyTelegramTheme = () => {
      const root = document.documentElement
      const scheme = isTelegramRuntime
        ? resolveTelegramColorScheme(tg)
        : (devTheme || "dark")
      const tp = tg?.themeParams

      root.dataset.tgColorScheme = scheme
      root.style.colorScheme = scheme

      const setThemeVar = (name: string, value?: string) => {
        if (value) root.style.setProperty(name, value)
        else root.style.removeProperty(name)
      }

      // Telegram ThemeParams own all non-brand runtime colors.
      setThemeVar("--tg-theme-bg-color", tp?.bg_color)
      setThemeVar("--tg-theme-text-color", tp?.text_color)
      setThemeVar("--tg-theme-hint-color", tp?.hint_color)
      setThemeVar("--tg-theme-link-color", tp?.link_color)
      setThemeVar("--tg-theme-button-color", tp?.button_color)
      setThemeVar("--tg-theme-button-text-color", tp?.button_text_color)
      setThemeVar("--tg-theme-secondary-bg-color", tp?.secondary_bg_color)
      setThemeVar("--tg-theme-header-bg-color", tp?.header_bg_color)
      setThemeVar("--tg-theme-bottom-bar-bg-color", tp?.bottom_bar_bg_color)
      setThemeVar("--tg-theme-accent-text-color", tp?.accent_text_color)
      setThemeVar("--tg-theme-section-bg-color", tp?.section_bg_color)
      setThemeVar("--tg-theme-section-header-text-color", tp?.section_header_text_color)
      setThemeVar("--tg-theme-section-separator-color", tp?.section_separator_color)
      setThemeVar("--tg-theme-subtitle-text-color", tp?.subtitle_text_color)
      setThemeVar("--tg-theme-destructive-text-color", tp?.destructive_text_color)

      // Critical neutral surfaces are emitted as concrete HEX values so old
      // Android WebViews never depend on CSS color-mix() support.
      const sectionSurface = tp?.section_bg_color || tp?.bg_color
      const headerSurface = tp?.header_bg_color || tp?.bg_color
      const themeText = tp?.text_color
      setThemeVar(
        "--bb-runtime-section-control-raised",
        mixTelegramHex(sectionSurface, themeText, 0.12),
      )
      setThemeVar(
        "--bb-runtime-header-control-raised",
        mixTelegramHex(headerSurface, themeText, 0.12),
      )
      setThemeVar(
        "--bb-runtime-control-border",
        mixTelegramHex(sectionSurface, themeText, 0.22),
      )

      if (isTelegramRuntime && tg && tp) {
        try {
          const headerColor = tp.header_bg_color || tp.bg_color
          if (headerColor) tg.setHeaderColor(headerColor)
        } catch { /* Best-effort Telegram API call. */ }
        try {
          const backgroundColor = tp.secondary_bg_color || tp.bg_color
          if (backgroundColor) tg.setBackgroundColor(backgroundColor)
        } catch { /* Best-effort Telegram API call. */ }
        try {
          const bottomBarColor = tp.bottom_bar_bg_color || tp.secondary_bg_color || tp.bg_color
          if (bottomBarColor) tg.setBottomBarColor?.(bottomBarColor)
        } catch { /* Best-effort Telegram API call. */ }
      }
    }

    applyTelegramTheme()
    tg?.onEvent("themeChanged", applyTelegramTheme)

    return () => {
      tg?.offEvent("themeChanged", applyTelegramTheme)
    }
  }, [tg])

  useEffect(() => {
    let disposed = false
    let authInFlight: Promise<void> | null = null
    let lastAuthStartedAt = 0

    const authenticateCurrentTelegramSession = async (resume = false) => {
      if (authInFlight) return authInFlight

      const now = Date.now()
      if (resume && now - lastAuthStartedAt < 1000) return

      // Dev fallback is allowed only in a real Vite development build. A production
      // bundle can never silently authenticate as the dev user.
      const devAuth = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === "true"
      const authData = tg?.initData || initData || (devAuth ? "dev" : "")

      if (!authData) {
        if (!disposed) setAuthError(t("auth.telegramRequired"))
        return
      }

      lastAuthStartedAt = now
      if (!disposed) setAuthError("")

      const run = login(authData, createLaunchId())
        .catch((err: any) => {
          if (!disposed) setAuthError(err.message || t("auth.authorizationError"))
        })
        .finally(() => {
          if (authInFlight === run) authInFlight = null
        })

      authInFlight = run
      return run
    }

    // Fresh page load.
    void authenticateCurrentTelegramSession(false)

    // Telegram Desktop and mobile may keep the WebView alive when the Mini App is
    // closed/minimized. On resume there is no page reload, so re-confirm the current
    // Telegram initData before allowing personal state to remain on screen.
    const handleActivated = () => { void authenticateCurrentTelegramSession(true) }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void authenticateCurrentTelegramSession(true)
      }
    }

    tg?.onEvent("activated", handleActivated)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      disposed = true
      tg?.offEvent("activated", handleActivated)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [initData, login, tg])

  useEffect(() => {
    if (!isAuthenticated) return

    const recordActivity = () => {
      if (document.visibilityState === "visible") void heartbeat()
    }
    recordActivity()
    const intervalId = window.setInterval(recordActivity, 5 * 60 * 1000)
    document.addEventListener("visibilitychange", recordActivity)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener("visibilitychange", recordActivity)
    }
  }, [heartbeat, isAuthenticated])

  if (orientationBlocked) {
    return <PortraitOnlyScreen />
  }

  if (authError) {
    return <ErrorScreen message={authError} />
  }

  if (isLoading || (!isAuthenticated && !authError)) {
    return <LoadingScreen />
  }

  return (
    <Routes>
      <Route path="/" element={<SearchPage />} />
      <Route path="/vehicle/:vehicleId" element={<SearchPage />} />
      <Route path="/garage/:vehicleId" element={<GaragePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/autoclan" element={<OwnGarageRoute requireActive><AutoclanPage /></OwnGarageRoute>} />
      <Route path="/partner" element={<PartnerPage />} />
      <Route path="/commercial/:direction" element={<OwnGarageRoute requireActive><CommercialPage /></OwnGarageRoute>} />
      <Route path="/garage-entry" element={<GarageEntryPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}
